import pluralize from 'pluralize';
import xorWith from 'lodash/xorWith';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';


// Clones a Class instance, with all methods, properties, getters & setters
export function cloneInstance(instance) {
  return Object.assign(Object.create(Object.getPrototypeOf(instance)), instance);
}

// react-notification-system settings
const baseNotificationSettings = {
  position: 'br',
  autoDismiss: 4,
  dismissible: false,
};

export const infoNotificationSettings = Object.assign({}, baseNotificationSettings, {
  level: 'info',
});

export const errorNotificationSettings = Object.assign({}, baseNotificationSettings, {
  title: '🔥 Error',
  level: 'error',
});

export const successNotificationSettings = Object.assign({}, baseNotificationSettings, {
  title: '✅ Success',
  level: 'success',
});

export const notificationStyles = {
  NotificationItem: {
    DefaultStyle: {
      backgroundColor: '#fff',
      color: 'rgba(0,0,0,0.84)',
      boxShadow: '0 0 20px 0 rgba(0,0,0,0.1)',
      borderTop: 'none',
    },
  },
  Title: {
    DefaultStyle: {
      fontSize: '13px',
      lineHeight: 1.3,
      fontWeight: 'normal',
      fontFamily: 'TeXGyreHerosRegular',
      fontStyle: 'normal',
      textTransform: 'uppercase',
    },
    success: {
      color: '#22d290',
    },
    error: {
      color: '#ff4b1f',
    },
    info: {
      color: '#859DAF',
    },
  },
  Containers: {
    DefaultStyle: {
      width: '250px',
    },
  },
};

export const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}/igm;

export const urlRegex = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g;

export const randomIdGenerator = () => Math.random().toFixed(5);

/*
  Styles to be consumed by `react-modal`
  See: https://github.com/reactjs/react-modal/blob/master/README.md
  We need `overflow: visible` in cases where `MediaItemInput` and `NotificationSystem`
  are sibling components.
*/
export const modalStyles = {
  content: {
    overflow: 'visible',
  },
};

/*
- Description:
  Use it to convert array of options to readable list.
- Usage:
  const listOfColors = ['White', 'Yellow', 'Blue'];
  listToReadableString(
    listOfColors,
    {
      emptyValue: 'All Colors.',
      serialComma: true,
      suffix: '.',
    }
  );
- Output:
  'White, Yellow, and Blue.'
*/
export const arrayToReadableString = (options, {
    emptyValue = '',
    prefix = '',
    suffix = '',
    textField = '',
    serialComma = false }) => {
  if (!options || !options.length || !Array.isArray(options)) {
    return emptyValue;
  }
  let list = null;
  let str = '';

  // Constructs a list of strings from options parameter
  switch (typeof (options[0])) {
    case ('object'):
      list = options.map(option => option[textField]);
      break;
    default:
      list = options;
  }

  // Adds a serial comma i.e. green, white, and blue.
  if (serialComma && list.length >= 3) {
    str = `${[...list.slice(0, list.length - 1)].join(', ')}, and ${list[list.length - 1]}`;
  } else if (serialComma && list.length === 2) {
    str = list.join(' and ');
  } else {
    str = list.join(', ');
  }

  return `${prefix}${str}${suffix}`;
};

/*
- Description:
  Use it to construct a GraphQL parameter from an option object that has label and value.
- Params:
  inline param: if yes: used for GraphQL string manipulating (no GraphQL variables)
    if no used for GraphQL variables
  resultType param: if string, parse the result to string: like 10 => "10"
- Usage:
  const selectedLocation = { label: 'New York', value:'new-york' };
  prepareOptionAsGqlParameter(selectedLocation, 'value', true, 'string');
- Output:
  `"new-york"`
  case 2:
  const selectedLocation = { label: 'New York', value:10 };
  prepareOptionAsGqlParameter(selectedLocation, 'value', false, 'number');
- Output:
  10
*/
export const prepareOptionAsGqlParameter = (option, value = 'value', inline = true, resultType = 'string') => {
  let result = option && option[value] ? option[value] : '';
  if (resultType === 'string') {
    result = `${result}`;
  }
  if (inline) {
    result = JSON.stringify(result);
  }
  return result || null;
};

/*
- Description:
  Use it to construct a GraphQL parameter from an array of option objects,
  that each has key and value.
- Params:
  inline param: if yes: used for GraphQL string manipulating (no GraphQL variables)
    if no used for GraphQL variables
  resultType param: if string, parse the result to string: like 10 => "10"
- Usage:
  const selectedLocations = [
    { label: 'New York', value:'new-york' },
    { label: 'Los Angeles', value:'los-angeles' },
  ];
  prepareOptionsAsGqlParameter(selectedLocations, value='value', true, 'string');
- Output:
  `["new-york","los-angeles"]`
  case 2:
  const selectedLocations = [
    { label: 'New York', value:'new-york' },
    { label: 'Los Angeles', value:'los-angeles' },
  ];
  prepareOptionsAsGqlParameter(selectedLocations, 'value', false, 'number');
- Output:
  [10]
*/
export const prepareOptionsAsGqlParameter = (options, valueProperty = 'value', inline = true, resultType = 'string') => {
  let results = options
    ? options.map(option => (resultType === 'string' ? `${option[valueProperty] || ''}` : option[valueProperty]))
    : [];
  if (inline) {
    results = JSON.stringify(results);
  }
  return results;
};

/*
- Description:
  Use it to construct react-select option object with key, value, and any custom properties.
  Custom properties can be used to get better insight about the selected value, especially
  when the select contains options that should be treated differently.
- Usage:
  const type = ['Private House', 'private-house'];
  prepareOption(type[0], type[1], {customProperty: 'customValue'});
- Output:
  {
    label: 'Private House',
    value: 'private-house',
    customProperty: 'customValue',
  }
*/
export const prepareOption = (label, value = label, custom = {}) => {
  const option = {
    label: String(label),
    value: String(value).replace(/"/g, '\\"'),
  };
  if (custom) {
    Object.assign(option, custom);
  }
  return option;
};

/*
- Description:
  Use it to construct react-select array of option objects.
- Usage:
  const types = ['Private House', 'Residential'];
  prepareOptions(types);
- Output:
  [
    {
      label: 'Private House',
      value: 'Private House',
    },
    {
      label: 'Residential',
      value: 'Residential',
    },
  ]
*/
export const prepareOptions = (options = [], labelProperty, valueProperty) => (
  options.map(option => prepareOption(
    typeof option === 'string' ? option : option[labelProperty],
    typeof option === 'string' ? option : option[valueProperty],
  ))
);

/*
- Description:
  Use it to pluralize last word of an array of sentences.
  Define the sentences you want to ignore in [ignore] array.
  Define the words that you want to convert before pluralization
    in [convert] as { oldWord: newWord }
- Usage:
  const ignore ['Blue Bell'];
  const convert { Development: Developer };
  const sentances = [
    'Blue Bell',
    'Red Apple',
    'Real Estate Development',
  ];
  pluralizeLastWord(sentances, ignore, sentances);
- Output:
  [
    'Blue Bell',
    'Red Apples',
    'Real Estate Developers',
  ]
*/
export const pluralizeLastWord = (sentences = [], ignore = [], convert = {}) => (
  sentences.map((sentence) => {
    if (ignore.includes(sentence)) {
      return sentence;
    }
    const words = sentence.split(' ');
    const lastWord = words.pop();
    return [...words, pluralize(convert[lastWord] ? convert[lastWord] : lastWord)].join(' ');
  })
);

/*
- Description:
  Use it to convert an int or a float to string with commas.
  empty string will be returned if n is invalid.
- Usage:
  numberWithCommas(1000);
  numberWithCommas(12237742.12);
- Output:
  '1,000'
  '12,237,742.12'
*/
export const numberWithCommas = n => (parseFloat(n, 10) || '').toLocaleString();

/*
- Description:
  Deep comparison between two arrays using lodash.
  Arrays can contain any values including objects.

  Return true when src and dist arraies are deeply equal, undefined otherwise.
- Usage:
  isArrayEqual([1, 2, 3], [1, 2, 3]);
  isArrayEqual(['a', 1], ['b']);
  isArrayEqual([{ id: 23 }], [{ id: 23 }]);
- Output:
  true
  undefined
  true
*/
export const isArrayEqual = (src, dist) => isEmpty(xorWith(src, dist, isEqual));

/**
 * Use it to transform array of objects into array of objects, each contain id and isActive
 * to highlight active item.
 * @param {Array} arr - array of objects
 * @param {String} idKey - id key name
 * @param {*} activeId - active id that will be highlighted
 * @returns {Array.<{id: *, isActive: boolean}>}
 * Returns new array of objects, each with id isActive
 * Returns empty array when missing arguments or id field is invalid
 * @example
 * highlightArrayActiveId([
 *  { value: 0, color: 'red' },
 *  { value: 1, color: 'blue' },
 *  { value: 2, color: 'green' },
 * ], 'value', 1)
 * // returns
 * [
 *  { id: 0 },
 *  { id: 1, isActive: true },
 *  { id: 2 },
 * ]
 */
export const highlightArrayActiveId = (arr, idKey, activeId) => {
  if (!arr || !arr.length || !idKey || !(idKey in arr[0])) {
    return [];
  }
  return arr.map(element => (
    element[idKey] === activeId
      ? { id: element[idKey], isActive: true }
      : { id: element[idKey] }
  ));
};

export function getUrlParameter(search, paramName) {
  if (!search || !paramName) {
    return null;
  }

  try {
    const param = paramName.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp(`[\\?&]${param}=([^&#]*)`);
    const results = regex.exec(search);
    return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
  } catch (e) {
    return null;
  }
}



// WEBPACK FOOTER //
// ./js/src/services/utils.js